import React from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

export type StepsModel = {
  title: string;
  id: number;
};

export type StepperModel = {
  steps: StepsModel[];
  activeStep: number;
  skippedActiveStep?: number;
  skippedStepFromStepper?: number;
  updateActiveStep: (step: number) => void;
  isLogin?: boolean;
  isSkipped?: boolean;
  iconCheck?: boolean;
  idReplace?: number;
  icon?: React.ReactNode;
  canClick?: boolean;
};

function getStepState(
  stepId: number,
  activeStep: number,
  isSkipped?: boolean,
  skippedActiveStep?: number
): 'active' | 'done' | 'skipped' | 'inactive' {
  if (activeStep === stepId) return 'active';
  if (activeStep > stepId) {
    if (isSkipped && stepId === skippedActiveStep) return 'skipped';
    return 'done';
  }
  return 'inactive';
}

/**
 * Multi-step progress indicator for wizard flows. Steps resolve to one of four
 * states — current, completed, upcoming, or skipped (rendered in destructive
 * text with no checkmark, driven by `isSkipped` / `skippedActiveStep`).
 *
 * @category Navigation
 */
export function Stepper({
  steps,
  activeStep,
  updateActiveStep,
  iconCheck,
  idReplace,
  icon,
  isSkipped,
  skippedActiveStep,
  skippedStepFromStepper,
  canClick = true
}: StepperModel) {
  const handleClick = (id: number) => {
    if (!canClick) return;
    if (activeStep > id && skippedStepFromStepper !== id) {
      updateActiveStep(id);
    }
  };

  const getDisplayContent = (id: number): React.ReactNode => {
    if (iconCheck && id === idReplace) return icon;
    if (iconCheck && idReplace != null && id > idReplace) return id - 1;
    return id;
  };

  return (
    <nav
      aria-label="Progress"
      className="flex h-full items-center bg-background px-5"
    >
      {steps.map((step, index) => {
        const state = getStepState(
          step.id,
          activeStep,
          isSkipped,
          skippedActiveStep
        );
        const isDone = state === 'done';
        const isActive = state === 'active';
        const isSkippedStep = state === 'skipped';
        const isClickable =
          canClick && activeStep > step.id && skippedStepFromStepper !== step.id;
        const isLast = index === steps.length - 1;
        const lineCompleted = isDone || isActive;

        return (
          <React.Fragment key={step.id}>
            {/* The step indicator is intentionally a non-interactive div for
                visual-only steps; clicks are wired only when isClickable to
                jump back to a completed step. Bank for follow-up: convert to
                a <button> + keyboard handler when the styling tradeoffs are
                worked out. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              onClick={() => isClickable && handleClick(step.id)}
              className={cn(
                'inline-flex h-6 items-center gap-2 shrink-0 select-none',
                isClickable ? 'cursor-pointer' : 'cursor-default'
              )}
            >
              <span
                className={cn(
                  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors',
                  (isActive || isDone || isSkippedStep) &&
                    'bg-primary text-primary-foreground',
                  state === 'inactive' &&
                    'bg-muted text-muted-foreground'
                )}
              >
                {isDone && !isSkippedStep ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                ) : (
                  getDisplayContent(step.id)
                )}
              </span>

              <span
                className={cn(
                  'whitespace-nowrap text-sm font-medium leading-6',
                  (isActive || isDone) && 'text-foreground',
                  isSkippedStep && 'text-destructive',
                  state === 'inactive' && 'text-muted-foreground'
                )}
              >
                {step.title}
              </span>
            </div>

            {!isLast && (
              <span
                aria-hidden="true"
                className={cn(
                  'mx-3 h-px min-w-4 flex-1 transition-colors',
                  lineCompleted ? 'bg-primary' : 'bg-border'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
